import { FormEvent, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { Check } from 'baseui/icon'
import { useSnackbar } from 'baseui/snackbar'

import {
  selectTeamsManagementCountrySites,
  selectTeamsManagementSelectedTeam,
} from '../TeamsManagementSelectors'
import { updateTeamsManagementSelectedTeam } from '../TeamsManagementSlice'
import { teamsManagementActions } from '../TeamsManagementSagas'
import { SelectedTeam, TeamFormErrors } from '../types'

interface UseAddEditModalParams {
  setIsOpen: (open: boolean) => void
}

interface BusinessVerticalOption {
  id: string
  label: string
}

interface UseAddEditModalReturn {
  t: TFunction
  formErrors: TeamFormErrors
  selectedTeam: SelectedTeam
  updateSelectedTeam: (teamInformation: Partial<SelectedTeam>) => void
  submitHandler: (event: FormEvent<HTMLFormElement>) => void
  businessVerticalOptions: BusinessVerticalOption[]
  nameOfBusinessVertical: (siteId: number | string | null | undefined) => string
}

const useAddEditModal = ({ setIsOpen }: UseAddEditModalParams): UseAddEditModalReturn => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [formErrors, setFormErrors] = useState<TeamFormErrors>({})
  const { enqueue } = useSnackbar()
  const selectedTeam = useSelector(selectTeamsManagementSelectedTeam)
  const countrySites = useSelector(selectTeamsManagementCountrySites)

  const businessVerticalOptions: BusinessVerticalOption[] = (countrySites ?? []).map(site => ({
    id: String(site.id),
    label: t(`teamsManagement:business_verticals.${site.business_type}`, site.business_type),
  }))

  const nameOfBusinessVertical = (siteId: number | string | null | undefined): string => {
    if (!siteId) {
      return t('common:choose')
    }
    const site = (countrySites ?? []).find(s => parseInt(String(s.id)) === parseInt(String(siteId)))
    if (!site) return t('common:choose')
    return t(`teamsManagement:business_verticals.${site.business_type}`, site.business_type)
  }

  const updateTeamSuccessNotification = (message: string) => {
    setIsOpen(false)
    const isEdit = !!selectedTeam.id
    const fallback = isEdit
      ? t('teamsManagement:success_update', 'Équipe mise à jour')
      : t('teamsManagement:success_create', 'Équipe créée')
    enqueue({
      message: message || fallback,
      startEnhancer: ({ size }: { size: number }) => <Check size={size} />,
    })
  }

  const updateSelectedTeam = (teamInformation: Partial<SelectedTeam>) => {
    dispatch(updateTeamsManagementSelectedTeam({ ...selectedTeam, ...teamInformation }))
  }

  const submitHandler = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const { id = 0, name, site_id } = selectedTeam

    const teamData = { name, site_id }

    if (id) {
      const teamDataWithId = { ...teamData, id }
      dispatch({
        type: teamsManagementActions.UPDATE_TEAM,
        teamData: teamDataWithId,
        updateTeamSuccessNotification,
        setFormErrors,
      })
      return
    }

    dispatch({
      type: teamsManagementActions.CREATE_TEAM,
      teamData,
      updateTeamSuccessNotification,
      setFormErrors,
    })
  }

  return {
    t,
    formErrors,
    selectedTeam,
    updateSelectedTeam,
    submitHandler,
    businessVerticalOptions,
    nameOfBusinessVertical,
  }
}

export default useAddEditModal
